import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateCodeVerifierAndChallenge, buildAuthUrl } from '../utils/oidc';
import { getOAuthRedirectUri } from '../utils/oauthRedirectOrigin';
import { Button, Card, Stack, Text } from '../ui';

export default function Login() {
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const n = sessionStorage.getItem('signup_login_notice_v1');
      if (n) {
        sessionStorage.removeItem('signup_login_notice_v1');
        setError(n);
      }
    } catch {}
  }, []);
  // Cognito is the only supported login mechanism in all environments (including local dev).
  // If these env vars are not set, we deliberately refuse to show legacy flows so
  // misconfiguration surfaces quickly instead of silently falling back.
  const hasCognito = Boolean(process.env.REACT_APP_COGNITO_DOMAIN && process.env.REACT_APP_COGNITO_CLIENT_ID);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--mist-100)' }}>
      <Card style={{ minWidth: 340, width: 380 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <img src="/logo.svg" alt="ProVault" style={{ height: 32 }} />
        </div>
        {!hasCognito && (
          <div className="pv-error" style={{ marginBottom: 24, textAlign: 'center' }}>
            Cognito login is required. Set <code>REACT_APP_COGNITO_DOMAIN</code> and <code>REACT_APP_COGNITO_CLIENT_ID</code>{' '}
            in <code>client/.env</code> (and the matching <code>COGNITO_*</code> vars for the backend), then restart the dev server.
          </div>
        )}
        {hasCognito && (
          <Stack gap={12}>
            <Text as="div" align="center" style={{ marginBottom: 8 }}>
              Sign in to your account
            </Text>
            <Button
              type="button"
              onClick={async () => {
                setError('');
                const { verifier, challenge } = await generateCodeVerifierAndChallenge();
                sessionStorage.setItem('pkce_verifier', verifier);
                const domain = (typeof window !== 'undefined' && window.__COGNITO_DOMAIN__) || process.env.REACT_APP_COGNITO_DOMAIN;
                const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
                const redirectUri = getOAuthRedirectUri();
                const scope = 'openid email profile';
                // If the user has explicitly logged out we set a short‑lived flag
                // in sessionStorage. Honour that here by forcing Cognito to show
                // the login form again even if an SSO session exists.
                const forceReauth =
                  typeof window !== 'undefined' &&
                  window.sessionStorage.getItem('force_reauth') === '1';
                const url = buildAuthUrl({
                  domain,
                  clientId,
                  redirectUri,
                  scope,
                  codeChallenge: challenge,
                  prompt: forceReauth ? 'login' : undefined,
                });
                window.location.assign(url);
              }}
              variant="primary"
              style={{ marginTop: 8, width: '100%' }}
            >
              Continue
            </Button>
            {error && <div role="alert" className="pv-error" style={{ textAlign: 'center' }}>{error}</div>}
          </Stack>
        )}
        <div style={{ textAlign: 'center', marginTop: 12, fontSize: 14, display: 'grid', gap: 6 }}>
          <div>
            New to ProVault?{' '}
            <button
              type="button"
              onClick={() => navigate('/register')}
              style={{ background: 'none', border: 'none', color: 'var(--sage-600)', textDecoration: 'underline', cursor: 'pointer' }}
            >
              Create an account
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
} 